import { useState, useEffect } from 'react'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Copy,
  Search,
  X,
  Clock,
  Target,
  Dumbbell
} from 'lucide-react'
import { useProgramStore } from '@/stores/programStore'
import { ProgramService } from '@/services'
import { Program, Workout, Exercise, Equipment, MuscleGroup } from '@/types'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

interface ProgramEditViewProps {
  programId: string
  onBack: () => void
  onSave: () => void
}

export function ProgramEditView({ programId, onBack, onSave }: ProgramEditViewProps) {
  const {
    setCurrentProgram,
    exerciseLibrary,
    setExerciseLibrary,
    exerciseFilters,
    setExerciseFilters
  } = useProgramStore()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editedProgram, setEditedProgram] = useState<Program | null>(null)
  const [selectedWeek, setSelectedWeek] = useState(1)
  const [selectedWorkout, setSelectedWorkout] = useState<string | null>(null)
  const [showExerciseLibrary, setShowExerciseLibrary] = useState(false)
  const [exerciseSearch, setExerciseSearch] = useState('')

  useEffect(() => {
    loadProgram()
    loadExerciseLibrary()
  }, [programId])

  const loadProgram = async () => {
    setLoading(true)
    try {
      const program = await ProgramService.getProgramById(programId)
      setCurrentProgram(program)
      setEditedProgram(JSON.parse(JSON.stringify(program))) // Deep clone
    } catch (error) {
      console.error('Failed to load program:', error)
      toast.error('Failed to load program')
    } finally {
      setLoading(false)
    }
  }

  const loadExerciseLibrary = async () => {
    try {
      const exercises = await ProgramService.getExerciseLibrary()
      setExerciseLibrary(exercises)
    } catch (error) {
      console.error('Failed to load exercise library:', error)
      toast.error('Failed to load exercise library')
    }
  }

  const handleSave = async () => {
    if (!editedProgram) return

    setSaving(true)
    try {
      const updated = await ProgramService.updateProgram(programId, editedProgram)
      setCurrentProgram(updated)
      toast.success('Program saved successfully')
      onSave()
    } catch (error) {
      console.error('Failed to save program:', error)
      toast.error('Failed to save program')
    } finally {
      setSaving(false)
    }
  }

  const handleProgramFieldChange = (field: keyof Program, value: any) => {
    if (!editedProgram) return
    
    setEditedProgram({
      ...editedProgram,
      [field]: value,
      updatedAt: new Date()
    })
  }

  const handleWorkoutChange = (weekIndex: number, workoutIndex: number, updates: Partial<Workout>) => {
    if (!editedProgram?.weeks) return

    const newWeeks = [...editedProgram.weeks]
    newWeeks[weekIndex].workouts[workoutIndex] = {
      ...newWeeks[weekIndex].workouts[workoutIndex],
      ...updates
    }

    setEditedProgram({
      ...editedProgram,
      weeks: newWeeks,
      updatedAt: new Date()
    })
  }

  const handleAddWorkout = (weekIndex: number) => {
    if (!editedProgram?.weeks) return

    const newWorkout: Workout = {
      id: `workout-${Date.now()}`,
      name: `New Workout`,
      day: 1,
      warmup: [],
      mainWork: [],
      cooldown: [],
      estimatedDuration: 60,
      difficulty: 5,
      equipment: [],
      completed: false
    }

    const newWeeks = [...editedProgram.weeks]
    newWeeks[weekIndex].workouts.push(newWorkout)

    setEditedProgram({
      ...editedProgram,
      weeks: newWeeks,
      updatedAt: new Date()
    })
  }

  const handleDeleteWorkout = (weekIndex: number, workoutIndex: number) => {
    if (!editedProgram?.weeks) return

    const newWeeks = [...editedProgram.weeks]
    newWeeks[weekIndex].workouts.splice(workoutIndex, 1)

    setEditedProgram({
      ...editedProgram,
      weeks: newWeeks,
      updatedAt: new Date()
    })
  }

  const handleDuplicateWorkout = (weekIndex: number, workoutIndex: number) => {
    if (!editedProgram?.weeks) return

    const originalWorkout = editedProgram.weeks[weekIndex].workouts[workoutIndex]
    const duplicatedWorkout: Workout = {
      ...JSON.parse(JSON.stringify(originalWorkout)),
      id: `workout-${Date.now()}`,
      name: `${originalWorkout.name} (Copy)`
    }

    const newWeeks = [...editedProgram.weeks]
    newWeeks[weekIndex].workouts.splice(workoutIndex + 1, 0, duplicatedWorkout)

    setEditedProgram({
      ...editedProgram,
      weeks: newWeeks,
      updatedAt: new Date()
    })
  }

  const handleAddExerciseToWorkout = (weekIndex: number, workoutIndex: number, exercise: Exercise) => {
    if (!editedProgram?.weeks) return

    const newExercise: Exercise = {
      ...exercise,
      id: `exercise-${Date.now()}`,
      sets: [
        {
          setNumber: 1,
          reps: 12,
          weight: undefined,
          completed: false
        }
      ]
    }

    const newWeeks = [...editedProgram.weeks]
    newWeeks[weekIndex].workouts[workoutIndex].mainWork.push(newExercise)

    setEditedProgram({
      ...editedProgram,
      weeks: newWeeks,
      updatedAt: new Date()
    })

    setShowExerciseLibrary(false)
  }

  const handleDeleteExercise = (weekIndex: number, workoutIndex: number, exerciseIndex: number) => {
    if (!editedProgram?.weeks) return

    const newWeeks = [...editedProgram.weeks]
    newWeeks[weekIndex].workouts[workoutIndex].mainWork.splice(exerciseIndex, 1)

    setEditedProgram({
      ...editedProgram,
      weeks: newWeeks,
      updatedAt: new Date()
    })
  }

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, type } = result

    if (!destination || !editedProgram?.weeks) return

    if (type === 'exercise') {
      // Handle exercise reordering within a workout
      const weekIndex = parseInt(source.droppableId.split('-')[1])
      const workoutIndex = parseInt(source.droppableId.split('-')[2])
      
      const newWeeks = [...editedProgram.weeks]
      const exercises = [...newWeeks[weekIndex].workouts[workoutIndex].mainWork]
      
      const [movedExercise] = exercises.splice(source.index, 1)
      exercises.splice(destination.index, 0, movedExercise)
      
      newWeeks[weekIndex].workouts[workoutIndex].mainWork = exercises

      setEditedProgram({
        ...editedProgram,
        weeks: newWeeks,
        updatedAt: new Date()
      })
    } else if (type === 'workout') {
      // Handle workout reordering within a week
      const weekIndex = parseInt(source.droppableId.split('-')[1])
      
      const newWeeks = [...editedProgram.weeks]
      const workouts = [...newWeeks[weekIndex].workouts]
      
      const [movedWorkout] = workouts.splice(source.index, 1)
      workouts.splice(destination.index, 0, movedWorkout)
      
      newWeeks[weekIndex].workouts = workouts

      setEditedProgram({
        ...editedProgram,
        weeks: newWeeks,
        updatedAt: new Date()
      })
    }
  }

  const filteredExercises = exerciseLibrary.filter(exercise => {
    const searchMatch = !exerciseSearch || 
      exercise.name.toLowerCase().includes(exerciseSearch.toLowerCase()) ||
      exercise.muscleGroups?.some(mg => mg.toLowerCase().includes(exerciseSearch.toLowerCase()))

    const muscleGroupMatch = !exerciseFilters.muscleGroup || 
      exercise.muscleGroups?.includes(exerciseFilters.muscleGroup as MuscleGroup)

    const equipmentMatch = !exerciseFilters.equipment || 
      exercise.equipment?.includes(exerciseFilters.equipment as Equipment)

    return searchMatch && muscleGroupMatch && equipmentMatch
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!editedProgram) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Program not found</h3>
        <button onClick={onBack} className="coach-button-primary px-4 py-2">
          Back
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Program</h1>
            <p className="text-gray-600">Make changes to your program structure and workouts</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowExerciseLibrary(true)}
            className="coach-button-secondary px-4 py-2 flex items-center"
          >
            <Dumbbell className="w-4 h-4 mr-2" />
            Exercise Library
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="coach-button-primary px-4 py-2 flex items-center disabled:opacity-50"
          >
            {saving ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {/* Program Details */}
      <div className="coach-section space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Program Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Program Name
            </label>
            <input
              type="text"
              value={editedProgram.name}
              onChange={(e) => handleProgramFieldChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (weeks)
            </label>
            <input
              type="number"
              min="1"
              max="52"
              value={editedProgram.duration}
              onChange={(e) => handleProgramFieldChange('duration', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={editedProgram.description || ''}
            onChange={(e) => handleProgramFieldChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Week Selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700">Editing Week:</label>
        <select
          value={selectedWeek}
          onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {Array.from({ length: editedProgram.duration }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              Week {i + 1}
            </option>
          ))}
        </select>
        <button
          onClick={() => handleAddWorkout(selectedWeek - 1)}
          className="coach-button-secondary px-3 py-2 flex items-center"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Workout
        </button>
      </div>

      {/* Drag and Drop Context */}
      <DragDropContext onDragEnd={handleDragEnd}>
        {/* Workouts for Selected Week */}
        {editedProgram.weeks && editedProgram.weeks[selectedWeek - 1] && (
          <Droppable droppableId={`week-${selectedWeek - 1}`} type="workout">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-4"
              >
                {editedProgram.weeks[selectedWeek - 1].workouts.map((workout, workoutIndex) => (
                  <Draggable
                    key={workout.id}
                    draggableId={workout.id}
                    index={workoutIndex}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`coach-section ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                      >
                        {/* Workout Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div
                              {...provided.dragHandleProps}
                              className="p-1 text-gray-400 hover:text-gray-600 cursor-grab"
                            >
                              <GripVertical className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={workout.name}
                                onChange={(e) => handleWorkoutChange(selectedWeek - 1, workoutIndex, { name: e.target.value })}
                                className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                              />
                              <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  <input
                                    type="number"
                                    value={workout.estimatedDuration}
                                    onChange={(e) => handleWorkoutChange(selectedWeek - 1, workoutIndex, { estimatedDuration: parseInt(e.target.value) })}
                                    className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
                                  />
                                  min
                                </div>
                                <div className="flex items-center gap-1">
                                  <Target className="w-4 h-4" />
                                  Difficulty:
                                  <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={workout.difficulty}
                                    onChange={(e) => handleWorkoutChange(selectedWeek - 1, workoutIndex, { difficulty: parseInt(e.target.value) })}
                                    className="w-12 px-1 py-0.5 border border-gray-300 rounded text-xs"
                                  />
                                  /10
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDuplicateWorkout(selectedWeek - 1, workoutIndex)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                              title="Duplicate Workout"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteWorkout(selectedWeek - 1, workoutIndex)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Delete Workout"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Exercises */}
                        <Droppable droppableId={`week-${selectedWeek - 1}-workout-${workoutIndex}`} type="exercise">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="space-y-2"
                            >
                              {workout.mainWork.map((exercise, exerciseIndex) => (
                                <Draggable
                                  key={exercise.id}
                                  draggableId={exercise.id}
                                  index={exerciseIndex}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg ${
                                        snapshot.isDragging ? 'shadow-lg' : ''
                                      }`}
                                    >
                                      <div className="flex items-center gap-3 flex-1">
                                        <div
                                          {...provided.dragHandleProps}
                                          className="p-1 text-gray-400 hover:text-gray-600 cursor-grab"
                                        >
                                          <GripVertical className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="font-medium text-gray-900">{exercise.name}</div>
                                          <div className="text-sm text-gray-600">
                                            {exercise.sets?.length || 0} sets • {exercise.muscleGroups?.join(', ')}
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => handleDeleteExercise(selectedWeek - 1, workoutIndex, exerciseIndex)}
                                        className="p-1 text-gray-400 hover:text-red-600"
                                        title="Remove Exercise"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                              
                              {workout.mainWork.length === 0 && (
                                <div className="text-center text-gray-500 py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                  No exercises yet. Add exercises from the library or drag them here.
                                </div>
                              )}

                              <button
                                onClick={() => {
                                  setSelectedWorkout(`${selectedWeek - 1}-${workoutIndex}`)
                                  setShowExerciseLibrary(true)
                                }}
                                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Exercise
                              </button>
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        )}
      </DragDropContext>

      {/* Exercise Library Modal */}
      {showExerciseLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Exercise Library</h2>
                <button
                  onClick={() => setShowExerciseLibrary(false)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Search and Filters */}
              <div className="mt-4 space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search exercises..."
                    value={exerciseSearch}
                    onChange={(e) => setExerciseSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex gap-4">
                  <select
                    value={exerciseFilters.muscleGroup || ''}
                    onChange={(e) => setExerciseFilters({ muscleGroup: e.target.value || undefined })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Muscle Groups</option>
                    <option value="chest">Chest</option>
                    <option value="back">Back</option>
                    <option value="shoulders">Shoulders</option>
                    <option value="biceps">Biceps</option>
                    <option value="triceps">Triceps</option>
                    <option value="abs">Abs</option>
                    <option value="quads">Quads</option>
                    <option value="hamstrings">Hamstrings</option>
                    <option value="glutes">Glutes</option>
                    <option value="calves">Calves</option>
                  </select>
                  
                  <select
                    value={exerciseFilters.equipment || ''}
                    onChange={(e) => setExerciseFilters({ equipment: e.target.value || undefined })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Equipment</option>
                    <option value="bodyweight">Bodyweight</option>
                    <option value="dumbbells">Dumbbells</option>
                    <option value="barbell">Barbell</option>
                    <option value="kettlebell">Kettlebell</option>
                    <option value="cable-machine">Cable Machine</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredExercises.map((exercise) => (
                  <div
                    key={exercise.id}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 cursor-pointer"
                    onClick={() => {
                      if (selectedWorkout) {
                        const [weekIndex, workoutIndex] = selectedWorkout.split('-').map(Number)
                        handleAddExerciseToWorkout(weekIndex, workoutIndex, exercise)
                      }
                    }}
                  >
                    <div className="font-medium text-gray-900">{exercise.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {exercise.muscleGroups?.join(', ')}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {exercise.equipment?.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredExercises.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  No exercises found matching your criteria
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}